import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Calendar, ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

function ClientWeekCalendar({ db, client }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      // Récupérer tous les employés sans filtre isArchived côté Firestore
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080'
      }));
      
      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const filteredEmployees = fetchedEmployees.filter(employee => {
        // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
        if (employee.role === 'beneficiaire') {
          return false;
        }
        
        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;
        
        return isNotArchived && isActive;
      });
      
      console.log('🔍 Employés récupérés dans ClientWeekCalendar:', {
        total: fetchedEmployees.length,
        filtrés: filteredEmployees.length,
        détails: fetchedEmployees.map(emp => ({
          id: emp.userId,
          nom: emp.name || `${emp.firstName} ${emp.lastName}`,
          isArchived: emp.isArchived,
          isActive: emp.isActive,
          role: emp.role
        }))
      });
      
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db]);

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    if (employee) {
      return employee.name || `${employee.firstName} ${employee.lastName}`;
    } else {
      console.warn(`Employé non trouvé pour l'ID: ${employeeId}`);
      return "Employé non trouvé";
    }
  };

  const getEmployeeColor = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee?.color || '#808080'; // Couleur par défaut si non trouvée
  };

  useEffect(() => {
    fetchEmployees();
    loadAppointments();
  }, [client, currentDate, fetchEmployees]);

  const loadAppointments = async () => {
    try {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Lundi
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Dimanche
      endOfWeek.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef, 
        where('clientId', '==', client.id),
        where('date', '>=', startOfWeek),
        where('date', '<=', endOfWeek)
      );
      
      const querySnapshot = await getDocs(q);
      const appointmentsData = [];
      
      querySnapshot.forEach((doc) => {
        const appointment = {
          id: doc.id,
          ...doc.data()
        };
        appointmentsData.push(appointment);
      });

      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const formatTime = (time) => {
    return time.substring(0, 5); // Format HH:MM
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Lundi
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(app => {
      const appDate = app.date.toDate();
      return appDate.toDateString() === date.toDateString();
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const getWeekRange = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      start: startOfWeek,
      end: endOfWeek
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  const weekDates = getWeekDates();
  const weekRange = getWeekRange();
  const weekTitle = `Semaine du ${formatDate(weekRange.start)} au ${formatDate(weekRange.end)}`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="mr-3 text-blue-600 dark:text-blue-400" size={24} />
          Planning de la semaine
        </h2>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronLeft size={20} />
            </button>
            
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {weekTitle}
            </span>
            
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-4 mb-6">
        {weekDates.map((date, index) => {
          const dayAppointments = getAppointmentsForDate(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const dayName = date.toLocaleDateString('fr-FR', { weekday: 'short' });
          
          return (
            <div key={index} className="text-center">
              <div className={`text-sm font-medium mb-2 ${
                isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
              }`}>
                {dayName}
              </div>
              <div className={`text-lg font-bold p-2 rounded-full ${
                isToday 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
              }`}>
                {date.getDate()}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {dayAppointments.length} RDV
              </div>
            </div>
          );
        })}
      </div>

      {/* Appointments Grid */}
      <div className="grid grid-cols-7 gap-4">
        {weekDates.map((date, dayIndex) => {
          const dayAppointments = getAppointmentsForDate(date);
          const isToday = date.toDateString() === new Date().toDateString();
          
          return (
            <div
              key={dayIndex}
              className={`min-h-48 p-3 rounded-lg ${
                isToday 
                  ? 'bg-blue-50 dark:bg-blue-900 border-2 border-blue-300 dark:border-blue-600' 
                  : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className="text-center mb-3">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {date.toLocaleDateString('fr-FR', { weekday: 'long' })}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(date)}
                </div>
              </div>

              <div className="space-y-2">
                {dayAppointments.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 text-sm py-4">
                    <Calendar size={20} className="mx-auto mb-1" />
                    Aucun RDV
                  </div>
                ) : (
                  dayAppointments.map((appointment) => {
                    const employeeColor = appointment.employeeId ? getEmployeeColor(appointment.employeeId) : '#808080';
                    return (
                      <div
                        key={appointment.id}
                        className="bg-white dark:bg-gray-600 rounded-lg p-3 shadow-sm border-l-4 hover:shadow-md transition-all duration-200"
                        style={{ borderLeftColor: employeeColor }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" style={{ color: employeeColor }} />
                            <div className="text-xs font-bold text-gray-900 dark:text-white">
                              {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                            </div>
                          </div>
                          <div className={`w-2 h-2 rounded-full ${
                            appointment.status === 'confirmed'
                              ? 'bg-green-400'
                              : appointment.status === 'cancelled'
                              ? 'bg-red-400'
                              : 'bg-yellow-400'
                          }`}></div>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center text-xs text-gray-700 dark:text-gray-300">
                            <div
                              className="w-4 h-4 rounded-full mr-2 flex items-center justify-center"
                              style={{ backgroundColor: employeeColor }}
                            >
                              <User className="w-3 h-3 text-white" />
                            </div>
                            <span className="font-medium truncate">
                              {appointment.employeeId ? getEmployeeName(appointment.employeeId) : 'Intervenant non spécifié'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {appointment.serviceType}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Week Summary */}
      {appointments.length > 0 && (
        <div className="mt-8 pt-6 border-t dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Résumé de la semaine</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{appointments.length}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">RDV total</p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900 rounded-lg">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {appointments.filter(a => a.status === 'confirmed').length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Confirmés</p>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {new Set(appointments.map(a => a.employeeId)).size}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Intervenants</p>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900 rounded-lg">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {new Set(appointments.map(a => a.date.toDate().toDateString())).size}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Jours avec RDV</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientWeekCalendar;
